import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { UploadFailureReason, TERMINAL_UPLOAD_STATUSES } from '../../../../../backend/src/types/types';
import type { UploadProcessingStatus } from '../../../../../backend/src/types/types';
import { Icon } from '../icon';
import { UploadHistory } from '@/api/types/backend-dto-types';
import { useMultiUploadProgress, UploadProgressState } from '@/hooks/useUploadProgress';
import { cn } from '@/new/lib/utils';
import { UploadDetailModal } from '@/new/pages/matters/:matterId/settings/file-history/components/modals/upload-detail-modal';

const SKIPPED_REASONS: UploadFailureReason[] = [
  UploadFailureReason.Duplicate,
  UploadFailureReason.UnsupportedFileFormat,
];

const ROW_MOTION = {
  initial: { opacity: 0, height: 0, y: -8 },
  animate: { opacity: 1, height: 'auto', y: 0 },
  exit: { opacity: 0, height: 0, y: -8 },
  transition: { duration: 0.4, ease: 'easeOut' },
} as const;

function isSkippedReason(reason: UploadFailureReason): boolean {
  return SKIPPED_REASONS.includes(reason);
}

export interface UploadSessionFailure {
  filename: string;
  reason: string;
  type: 'skipped' | 'failed';
}

export interface UploadSession {
  uploadId: string;
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  failures: UploadSessionFailure[];
  isS3Complete: boolean;
}

type UploadStatus = UploadProcessingStatus | 'idle' | 'uploading';

/** Debounces status transitions with a minimum 1s gap and a 150ms fade animation. */
function useDebouncedStatus(currentStatus: UploadStatus) {
  const [displayedStatus, setDisplayedStatus] = useState<UploadStatus>('idle');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousRef = useRef<UploadStatus>('idle');
  const lastTransitionRef = useRef(Date.now());

  useEffect(() => {
    if (currentStatus === previousRef.current) {
      // Sync if displayedStatus is stale (e.g. first render with a non-idle status)
      if (currentStatus !== 'idle' && displayedStatus === 'idle') {
        setDisplayedStatus(currentStatus);
        previousRef.current = currentStatus;
        lastTransitionRef.current = Date.now();
      }
      return;
    }

    previousRef.current = currentStatus;

    if (currentStatus === 'idle') {
      setDisplayedStatus('idle');
      return;
    }

    // First real status — snap immediately, no animation
    if (displayedStatus === 'idle') {
      setDisplayedStatus(currentStatus);
      lastTransitionRef.current = Date.now();
      return;
    }

    // Uploading → processing: snap immediately, CSS transitions handle the visual change
    if (displayedStatus === 'uploading' && currentStatus === 'processing') {
      setDisplayedStatus(currentStatus);
      lastTransitionRef.current = Date.now();
      return;
    }

    // Subsequent transitions — wait at least 1s from last, then fade
    const delay = Math.max(0, 1000 - (Date.now() - lastTransitionRef.current));
    const timer = setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setDisplayedStatus(currentStatus);
        setIsTransitioning(false);
        lastTransitionRef.current = Date.now();
      }, 150);
    }, delay);
    return () => clearTimeout(timer);
  }, [currentStatus, displayedStatus]);

  return { displayedStatus, isTransitioning };
}

interface StatusRowProps {
  icon: 'spinner' | 'check' | 'alert';
  text: string;
  textColor: string;
  truncateText?: boolean;
  progress?: {
    value: number;
    text: string;
    bgColor: string;
    fillColor: string;
  };
  link?: {
    label: string;
  } & ({ to: string; onClick?: never } | { onClick: () => void; to?: never });
}

function StatusRow({ icon, text, textColor, truncateText, progress, link }: StatusRowProps) {
  const renderIcon = () => {
    if (icon === 'spinner') {
      return (
        <span className="relative flex h-[1em] w-[1em] shrink-0 items-center justify-center">
          <span className={cn('absolute h-2 w-2 animate-ping rounded-full bg-current opacity-35', textColor)} />
          <span className={cn('h-1.5 w-1.5 rounded-full bg-current transition-colors duration-500', textColor)} />
        </span>
      );
    }
    const iconClass = icon === 'check' ? 'lucide--check' : 'lucide--alert-circle';
    return <Icon className={cn(iconClass, textColor, 'transition-colors duration-500')} />;
  };

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        {renderIcon()}
        <span className={cn('text-sm transition-colors duration-500', textColor, truncateText && 'truncate')}>
          {text}
        </span>
      </div>
      {progress && (
        <div className="flex flex-1 items-center gap-2">
          <div
            className={cn(
              'h-1.5 min-w-32 flex-1 overflow-hidden rounded-full transition-colors duration-500',
              progress.bgColor,
            )}
          >
            <div
              className={cn('h-full rounded-full transition-all duration-500', progress.fillColor)}
              style={{ width: `${progress.value}%` }}
            />
          </div>
          <span className={cn('text-sm whitespace-nowrap tabular-nums transition-colors duration-500', textColor)}>
            {progress.text}
          </span>
        </div>
      )}
      {link &&
        (link.to ? (
          <Link
            to={link.to}
            className={cn(
              'shrink-0 rounded-md px-2 py-0.5 text-xs font-medium transition-colors hover:opacity-80',
              textColor,
            )}
          >
            {link.label}
          </Link>
        ) : (
          <button
            onClick={link.onClick}
            className={cn(
              'shrink-0 rounded-md px-2 py-0.5 text-xs font-medium transition-colors hover:opacity-80',
              textColor,
            )}
          >
            {link.label}
          </button>
        ))}
    </div>
  );
}

const STATUS_COLORS = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    text: 'text-blue-600',
    textHover: 'hover:text-blue-700',
    progressBg: 'bg-blue-200',
    progressFill: 'bg-blue-500',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-300',
    text: 'text-green-600',
    textHover: 'hover:text-green-700',
    progressBg: 'bg-green-200',
    progressFill: 'bg-green-500',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-600',
    textHover: 'hover:text-amber-700',
    progressBg: 'bg-amber-200',
    progressFill: 'bg-amber-500',
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-600',
    textHover: 'hover:text-red-700',
    progressBg: 'bg-red-200',
    progressFill: 'bg-red-500',
  },
  gray: {
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    text: 'text-gray-600',
    textHover: 'hover:text-gray-700',
    progressBg: 'bg-gray-200',
    progressFill: 'bg-gray-500',
  },
};

function getStatusColor(status: UploadStatus): keyof typeof STATUS_COLORS {
  if (status === 'uploading') return 'blue';
  if (status === 'processing') return 'blue';
  if (status === 'failed') return 'red';
  if (status === 'cancelled') return 'amber';
  if (status === 'completed') return 'green';
  return 'gray';
}

// --- UploadStatusList ---

interface UploadStatusListProps {
  uploadSessions: UploadSession[];
  uploadHistory?: UploadHistory[];
  uppyProgressByUpload: Record<string, number>;
  matterId: string;
  onProcessingComplete?: (uploadId?: string) => void;
}

export function UploadStatusList({
  uploadSessions,
  uploadHistory,
  uppyProgressByUpload,
  matterId,
  onProcessingComplete,
}: UploadStatusListProps) {
  // Track IDs that have been auto-dismissed after showing their terminal state
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  // Track IDs we've seen as processing so they persist through the completed transition
  const seenProcessingRef = useRef<Set<string>>(new Set());

  const dismissUpload = useCallback((uploadId: string) => {
    setDismissedIds((prev) => new Set(prev).add(uploadId));
    seenProcessingRef.current.delete(uploadId);
  }, []);

  // Remember any upload we see as 'processing' so it stays visible after completion
  useEffect(() => {
    for (const u of uploadHistory ?? []) {
      if (u.status === 'processing') {
        seenProcessingRef.current.add(u.id);
      }
    }
  }, [uploadHistory]);

  const activeUploadIds = useMemo(() => {
    const seen = new Set<string>();
    const ids: string[] = [];

    for (const s of uploadSessions) {
      if (seen.has(s.uploadId) || dismissedIds.has(s.uploadId)) continue;
      seen.add(s.uploadId);
      ids.push(s.uploadId);
    }

    for (const u of uploadHistory ?? []) {
      // Show if currently processing OR if we previously saw it processing (terminal transition)
      if (u.status !== 'processing' && !seenProcessingRef.current.has(u.id)) continue;
      if (seen.has(u.id) || dismissedIds.has(u.id)) continue;
      seen.add(u.id);
      ids.push(u.id);
    }

    return ids;
  }, [uploadSessions, uploadHistory, dismissedIds]);

  // Only subscribe to SSE for uploads that haven't reached a terminal DB state
  const sseUploadIds = useMemo(() => {
    const historyMap = new Map((uploadHistory ?? []).map((u) => [u.id, u]));
    return activeUploadIds.filter((id) => {
      const h = historyMap.get(id);
      return !h || !h.status || !TERMINAL_UPLOAD_STATUSES.has(h.status);
    });
  }, [activeUploadIds, uploadHistory]);

  // Single multiplexed SSE connection for ALL active uploads
  const { stateMap } = useMultiUploadProgress(sseUploadIds);

  if (activeUploadIds.length === 0) {
    return null;
  }

  return (
    <div className="my-1.5 space-y-2">
      {activeUploadIds.map((uploadId) => {
        const session = uploadSessions.find((s) => s.uploadId === uploadId);
        return (
          <UploadStatusItem
            key={uploadId}
            uploadId={uploadId}
            session={session}
            currentUploadHistory={uploadHistory?.find((u) => u.id === uploadId)}
            sseProgress={stateMap.get(uploadId)}
            uppyProgress={uppyProgressByUpload[uploadId] ?? 0}
            matterId={matterId}
            onProcessingComplete={onProcessingComplete}
            onDismiss={dismissUpload}
          />
        );
      })}
    </div>
  );
}

// --- UploadStatusItem ---

const idleProgress: UploadProgressState = {
  status: 'idle',
  extractions: [],
  totalFiles: 0,
  hashedFiles: 0,
  duplicateCount: 0,
  duplicateIncludedCount: 0,
  unpackingSkippedCount: 0,
  textFallbackCount: 0,
  errors: [],
  downloadedFiles: 0,
  totalDownloadFiles: 0,
  preparingFetchedCount: 0,
  preparingTotalCount: 0,
};

interface UploadStatusItemProps {
  uploadId: string;
  session?: UploadSession;
  currentUploadHistory?: UploadHistory;
  sseProgress?: UploadProgressState;
  uppyProgress: number;
  matterId: string;
  onProcessingComplete?: (uploadId?: string) => void;
  onDismiss?: (uploadId: string) => void;
}

function UploadStatusItem({
  uploadId,
  session,
  currentUploadHistory,
  sseProgress: sseProgressProp,
  uppyProgress,
  matterId,
  onProcessingComplete,
  onDismiss,
}: UploadStatusItemProps) {
  const isS3Uploading = !!session && !session.isS3Complete;
  const sseProgress = sseProgressProp ?? idleProgress;
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // All files were skipped (unsupported format) — nothing was actually uploaded
  const allSkipped =
    session && session.failedFiles >= session.totalFiles && session.failures.every((f) => f.type === 'skipped');
  const allFailedS3 =
    session?.isS3Complete && session.completedFiles === 0 && session.failedFiles >= session.totalFiles;

  const dbStatus = currentUploadHistory?.status as UploadStatus | undefined;

  const sseCompletedButDbStale = sseProgress.status === 'completed' && dbStatus !== 'completed';

  // Priority: S3 uploading > DB terminal > client-only states > SSE > DB > fallback
  const currentStatus: UploadStatus = isS3Uploading
    ? 'uploading'
    : dbStatus === 'failed' || dbStatus === 'cancelled'
      ? dbStatus
      : allSkipped
        ? 'completed'
        : allFailedS3
          ? 'failed'
          : sseCompletedButDbStale
            ? 'processing'
            : sseProgress.status !== 'idle'
              ? sseProgress.status
              : (dbStatus ?? (session?.isS3Complete ? 'processing' : 'idle'));

  const { displayedStatus, isTransitioning } = useDebouncedStatus(currentStatus);

  // Notify parent when processing completes (e.g., to refetch files and remove session).
  // Fire on either SSE status or polled DB status reaching a terminal state, so the
  // polling fallback (after SSE has failed) still refreshes the files list. Latched
  // via ref so we only fire once per upload even if both signals reach terminal.
  const completionFiredRef = useRef(false);
  useEffect(() => {
    if (completionFiredRef.current) return;
    if (
      sseProgress.status === 'completed' ||
      sseProgress.status === 'failed' ||
      dbStatus === 'completed' ||
      dbStatus === 'failed' ||
      dbStatus === 'cancelled'
    ) {
      completionFiredRef.current = true;
      onProcessingComplete?.(uploadId);
    }
  }, [sseProgress.status, dbStatus, onProcessingComplete, uploadId]);

  // Auto-dismiss terminal status rows after a delay
  useEffect(() => {
    if (!onDismiss || session || detailModalOpen) return;

    let delay: number | null = null;
    if (displayedStatus === 'cancelled' || (allSkipped && !isS3Uploading)) {
      delay = 10_000;
    } else if (displayedStatus === 'completed' || displayedStatus === 'failed') {
      delay = 30_000;
    }

    if (delay === null) return;

    const timeout = setTimeout(() => onDismiss(uploadId), delay);
    return () => clearTimeout(timeout);
  }, [displayedStatus, allSkipped, isS3Uploading, onDismiss, uploadId, session, detailModalOpen]);

  // --- Colors ---
  const isDeduplicationDisabled = currentUploadHistory?.deduplicateDocuments === false;
  const duplicateIncludedCount = currentUploadHistory
    ? isDeduplicationDisabled
      ? currentUploadHistory.duplicateCount
      : 0
    : sseProgress.duplicateIncludedCount;
  const duplicateSkippedCount = currentUploadHistory
    ? isDeduplicationDisabled
      ? 0
      : currentUploadHistory.duplicateCount
    : sseProgress.duplicateCount;
  const allFailed = session
    ? session.failedFiles > 0 &&
      session.failedFiles >= session.totalFiles &&
      session.failures.some((f) => f.type === 'failed')
    : false;
  const uploadErrorCount = session?.failures.filter((f) => f.type === 'failed').length ?? 0;
  const clientSkippedCount = session?.failures.filter((f) => f.type === 'skipped').length ?? 0;
  const processingFileIssueCount = sseProgress.errors.filter((e) => isSkippedReason(e.rejectedReason)).length;
  const processingErrorCount = sseProgress.errors.filter((e) => !isSkippedReason(e.rejectedReason)).length;
  const terminalUploadErrorCount = currentUploadHistory
    ? (currentUploadHistory.uploadFailures ?? []).filter((f) => !isSkippedReason(f.rejectedReason)).length
    : uploadErrorCount + processingErrorCount;
  const terminalWarningCount = currentUploadHistory
    ? (currentUploadHistory.uploadFailures ?? []).filter((f) => isSkippedReason(f.rejectedReason)).length
    : duplicateSkippedCount + duplicateIncludedCount + processingFileIssueCount + clientSkippedCount;

  let colorKey = getStatusColor(displayedStatus);
  if (displayedStatus === 'uploading' && allFailed) colorKey = 'red';
  else if (displayedStatus === 'completed' && terminalUploadErrorCount > 0) colorKey = 'red';
  else if (displayedStatus === 'completed' && terminalWarningCount > 0) colorKey = 'amber';
  const colors = STATUS_COLORS[colorKey];

  const [processingNow, setProcessingNow] = useState(() => Date.now());
  useEffect(() => {
    if (displayedStatus !== 'processing' && displayedStatus !== 'uploading') {
      return;
    }

    const interval = setInterval(() => setProcessingNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, [displayedStatus]);

  const visibleExtractions = sseProgress.extractions.filter(
    (extraction) => !extraction.isComplete || (extraction.completedAt ?? 0) > processingNow - 3000,
  );
  // session.totalFiles = top-level files the user uploaded
  // sseProgress.totalFiles = additional files discovered during ZIP/email extraction
  const rawTotal = (session?.totalFiles ?? 0) + sseProgress.totalFiles;
  // Subtract processing errors from the total — those files failed extraction and will never be hashed
  const totalForDedup = Math.max(rawTotal - processingErrorCount, sseProgress.hashedFiles);
  const showDedupRow = totalForDedup > 0 && sseProgress.hashedFiles > 0;

  // --- Processing rows helpers ---
  const blueColors = STATUS_COLORS.blue;

  const showPreparingRow = sseProgress.preparingTotalCount > 0 && sseProgress.totalDownloadFiles === 0;

  const renderPreparingRow = (): React.ReactNode => {
    if (!showPreparingRow) return null;
    return (
      <motion.div key="preparing" {...ROW_MOTION}>
        <StatusRow
          icon="spinner"
          text="Fetching Relativity document list"
          textColor={blueColors.text}
          progress={{
            value: Math.round((sseProgress.preparingFetchedCount / sseProgress.preparingTotalCount) * 100),
            text: `${sseProgress.preparingFetchedCount.toLocaleString()} / ${sseProgress.preparingTotalCount.toLocaleString()}`,
            bgColor: blueColors.progressBg,
            fillColor: blueColors.progressFill,
          }}
        />
      </motion.div>
    );
  };

  const showDownloadRow = sseProgress.totalDownloadFiles > 0;

  const renderDownloadRow = (): React.ReactNode => {
    if (!showDownloadRow) return null;
    const isDone = sseProgress.downloadedFiles >= sseProgress.totalDownloadFiles;
    return (
      <motion.div key="download" {...ROW_MOTION}>
        <StatusRow
          icon={isDone ? 'check' : 'spinner'}
          text="Downloading from Relativity"
          textColor={blueColors.text}
          progress={{
            value: Math.round((sseProgress.downloadedFiles / sseProgress.totalDownloadFiles) * 100),
            text: `${sseProgress.downloadedFiles.toLocaleString()} / ${sseProgress.totalDownloadFiles.toLocaleString()}`,
            bgColor: blueColors.progressBg,
            fillColor: blueColors.progressFill,
          }}
        />
      </motion.div>
    );
  };

  const renderUnpackSkipRow = (): React.ReactNode => {
    if (sseProgress.unpackingSkippedCount === 0) return null;
    const count = sseProgress.unpackingSkippedCount;
    return (
      <motion.div key="unpack-skip" {...ROW_MOTION}>
        <StatusRow
          icon="check"
          text={`${count} email${count !== 1 ? 's' : ''} kept as single document${count !== 1 ? 's' : ''} (attachments not extracted)`}
          textColor={blueColors.text}
        />
      </motion.div>
    );
  };

  const renderTextFallbackRow = (): React.ReactNode => {
    if (sseProgress.textFallbackCount === 0) return null;
    const count = sseProgress.textFallbackCount;
    return (
      <motion.div key="text-fallback" {...ROW_MOTION}>
        <StatusRow
          icon="check"
          text={`${count} file${count !== 1 ? 's' : ''} fell back to extracted text (unsupported native format)`}
          textColor={blueColors.text}
        />
      </motion.div>
    );
  };

  const renderDuplicateIncludedRow = (): React.ReactNode => {
    if (duplicateIncludedCount === 0) return null;
    return (
      <motion.div key="duplicate-included" {...ROW_MOTION}>
        <StatusRow
          icon="check"
          text={`${duplicateIncludedCount} duplicate${duplicateIncludedCount !== 1 ? 's' : ''} included (deduplication disabled)`}
          textColor={blueColors.text}
        />
      </motion.div>
    );
  };

  const renderDedupRow = (): React.ReactNode => {
    if (!showDedupRow) return null;
    const duplicateProgressCount = sseProgress.duplicateCount + sseProgress.duplicateIncludedCount;
    return (
      <motion.div key="dedup" {...ROW_MOTION}>
        <StatusRow
          icon={sseProgress.hashedFiles >= totalForDedup ? 'check' : 'spinner'}
          text="Checking for duplicates"
          textColor={blueColors.text}
          progress={{
            value: Math.round((sseProgress.hashedFiles / totalForDedup) * 100),
            text:
              duplicateProgressCount > 0
                ? `${sseProgress.hashedFiles} checked — ${Math.max(0, sseProgress.hashedFiles - duplicateProgressCount)} new, ${duplicateProgressCount} duplicate${duplicateProgressCount !== 1 ? 's' : ''} ${
                    sseProgress.duplicateIncludedCount > 0 ? 'included' : 'skipped'
                  }`
                : `${sseProgress.hashedFiles} checked`,
            bgColor: blueColors.progressBg,
            fillColor: blueColors.progressFill,
          }}
        />
      </motion.div>
    );
  };

  const renderExtractionRows = ({ keepCompleted }: { keepCompleted?: boolean } = {}): React.ReactNode => {
    const extractions = keepCompleted ? sseProgress.extractions : visibleExtractions;
    if (extractions.length === 0 && processingErrorCount === 0) return null;

    return (
      <>
        <AnimatePresence initial={false}>
          {extractions.map((extraction) => {
            const extractPct =
              extraction.totalEntries > 0
                ? Math.round((extraction.completedEntries / extraction.totalEntries) * 100)
                : 0;
            const fileCountLabel =
              extraction.totalEntries > 0
                ? ` (${extraction.totalEntries} file${extraction.totalEntries !== 1 ? 's' : ''})`
                : '';

            return (
              <motion.div key={extraction.filename} {...ROW_MOTION}>
                <StatusRow
                  icon={extraction.isComplete ? 'check' : 'spinner'}
                  text={`Extracting ${extraction.filename}${fileCountLabel}`}
                  textColor={blueColors.text}
                  truncateText
                  progress={
                    extraction.totalEntries > 0
                      ? {
                          value: extraction.isComplete ? 100 : extractPct,
                          text: `${extraction.completedEntries} / ${extraction.totalEntries}`,
                          bgColor: blueColors.progressBg,
                          fillColor: blueColors.progressFill,
                        }
                      : undefined
                  }
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
        {processingErrorCount > 0 && (
          <StatusRow
            icon="alert"
            text={`${processingErrorCount} file${processingErrorCount !== 1 ? 's' : ''} failed processing`}
            textColor="text-red-500"
          />
        )}
      </>
    );
  };

  // --- Content rendering ---
  const renderContent = () => {
    // User-initiated upload: shared DOM tree for uploading → processing so colors transition via CSS
    if ((displayedStatus === 'uploading' || displayedStatus === 'processing') && session) {
      if (allFailed) {
        return (
          <StatusRow
            icon="alert"
            text={`Upload failed for ${session.failedFiles} file${session.failedFiles !== 1 ? 's' : ''}. Check your connection and try again.`}
            textColor="text-red-600"
          />
        );
      }

      const isUploadDone = session.isS3Complete;
      const uploadRowColors = STATUS_COLORS.blue;
      const dedupRow = renderDedupRow();
      const unpackSkipRow = renderUnpackSkipRow();
      const textFallbackRow = renderTextFallbackRow();
      const duplicateIncludedRow = renderDuplicateIncludedRow();
      const extractionRows = renderExtractionRows();
      // When SSE isn't delivering progress (silently blocked, or backend hasn't
      // started emitting yet), the row would otherwise sit on "Uploaded" until
      // polling flips dbStatus to terminal. Show a generic "Processing" fallback
      // so the user can see the backend is still working.
      const showProcessingFallback =
        isUploadDone && displayedStatus === 'processing' && !dedupRow && !duplicateIncludedRow && !extractionRows;

      return (
        <div className="flex w-full flex-col gap-2.5">
          <StatusRow
            icon={isUploadDone ? 'check' : 'spinner'}
            text={isUploadDone ? 'Uploaded' : 'Uploading'}
            textColor={uploadRowColors.text}
            progress={{
              value: isUploadDone ? 100 : uppyProgress,
              text:
                uploadErrorCount > 0
                  ? `${session.completedFiles} / ${session.totalFiles} — ${uploadErrorCount} error${uploadErrorCount !== 1 ? 's' : ''}`
                  : `${session.completedFiles} / ${session.totalFiles}`,
              bgColor: uploadRowColors.progressBg,
              fillColor: uploadRowColors.progressFill,
            }}
          />
          {uploadErrorCount > 0 && (
            <StatusRow
              icon="alert"
              text={`${uploadErrorCount} upload error${uploadErrorCount !== 1 ? 's' : ''}`}
              textColor="text-red-500"
            />
          )}
          {showProcessingFallback && <StatusRow icon="spinner" text="Processing" textColor={colors.text} />}
          <AnimatePresence initial={false}>
            {dedupRow}
            {duplicateIncludedRow}
            {unpackSkipRow}
            {textFallbackRow}
          </AnimatePresence>
          {extractionRows}
        </div>
      );
    }

    // Processing without session (e.g. page reload while processing, or Relativity import)
    if (displayedStatus === 'processing') {
      const preparingRow = renderPreparingRow();
      const downloadRow = renderDownloadRow();
      const dedupRow = renderDedupRow();
      const unpackSkipRow = renderUnpackSkipRow();
      const textFallbackRow = renderTextFallbackRow();
      const duplicateIncludedRow = renderDuplicateIncludedRow();
      const extractionRows = renderExtractionRows();
      const hasDetailRows =
        !!preparingRow ||
        !!downloadRow ||
        !!dedupRow ||
        !!duplicateIncludedRow ||
        !!unpackSkipRow ||
        !!textFallbackRow ||
        !!extractionRows;
      const shouldShowProcessingFallback = !hasDetailRows && currentUploadHistory?.status === 'processing';

      if (!hasDetailRows && !shouldShowProcessingFallback) {
        return null;
      }

      return (
        <div className="flex max-h-40 w-full flex-col gap-2.5 overflow-y-hidden">
          {shouldShowProcessingFallback && <StatusRow icon="spinner" text="Processing" textColor={colors.text} />}
          <AnimatePresence initial={false}>
            {preparingRow}
            {downloadRow}
            {dedupRow}
            {duplicateIncludedRow}
            {unpackSkipRow}
            {textFallbackRow}
          </AnimatePresence>
          {extractionRows}
        </div>
      );
    }

    if (displayedStatus === 'completed') {
      if (currentUploadHistory) {
        const filesAdded = currentUploadHistory.successfullyUploadedFileCount;
        const dupesSkipped = currentUploadHistory.deduplicateDocuments ? currentUploadHistory.duplicateCount : 0;
        const dupesIncluded = currentUploadHistory.deduplicateDocuments ? 0 : currentUploadHistory.duplicateCount;
        const unpackSkipped = sseProgress.unpackingSkippedCount;
        const textFallbackApplied = sseProgress.textFallbackCount;
        const unsupportedSkipped = (currentUploadHistory.uploadFailures ?? []).filter(
          (f) => f.rejectedReason === UploadFailureReason.UnsupportedFileFormat,
        ).length;
        const errorCount = (currentUploadHistory.uploadFailures ?? []).filter(
          (f) => !isSkippedReason(f.rejectedReason),
        ).length;

        const hasIssues = errorCount > 0 || unsupportedSkipped > 0 || dupesSkipped > 0 || dupesIncluded > 0;
        const parts = [`${filesAdded} file${filesAdded !== 1 ? 's' : ''} added`];
        if (dupesSkipped > 0) parts.push(`${dupesSkipped} duplicate${dupesSkipped !== 1 ? 's' : ''} skipped`);
        if (dupesIncluded > 0) parts.push(`${dupesIncluded} duplicate${dupesIncluded !== 1 ? 's' : ''} included`);
        if (unpackSkipped > 0) parts.push(`${unpackSkipped} email unpack${unpackSkipped !== 1 ? 's' : ''} skipped`);
        if (textFallbackApplied > 0)
          parts.push(`${textFallbackApplied} file${textFallbackApplied !== 1 ? 's' : ''} fell back to extracted text`);
        if (unsupportedSkipped > 0)
          parts.push(`${unsupportedSkipped} unsupported file${unsupportedSkipped > 1 ? 's' : ''}`);
        if (errorCount > 0) parts.push(`${errorCount} error${errorCount !== 1 ? 's' : ''}`);

        return (
          <StatusRow
            icon={hasIssues ? 'alert' : 'check'}
            text={filesAdded === 0 && !hasIssues ? 'No new files were added.' : `${parts.join('. ')}.`}
            textColor={colors.text}
            link={{ onClick: () => setDetailModalOpen(true), label: 'View details' }}
          />
        );
      }

      // All files were skipped — show count before DB data loads
      if (allSkipped && session) {
        const skippedCount = session.failures.length;
        return (
          <StatusRow
            icon="alert"
            text={`${skippedCount} file${skippedCount !== 1 ? 's' : ''} skipped.`}
            textColor={colors.text}
          />
        );
      }

      return <StatusRow icon="check" text="Upload complete." textColor={colors.text} />;
    }

    if (displayedStatus === 'cancelled') {
      return (
        <StatusRow
          icon="alert"
          text="Upload was cancelled. Any files already uploaded will still be processed."
          textColor={colors.text}
        />
      );
    }

    if (displayedStatus === 'failed') {
      return (
        <StatusRow
          icon="alert"
          text="Upload processing failed. Some files may not have been processed."
          textColor={colors.text}
          link={currentUploadHistory ? { onClick: () => setDetailModalOpen(true), label: 'View details' } : undefined}
        />
      );
    }

    return null;
  };

  const canDismiss =
    onDismiss && (displayedStatus === 'completed' || displayedStatus === 'failed' || displayedStatus === 'cancelled');

  if (displayedStatus === 'idle' && !isTransitioning) {
    return null;
  }

  const content = renderContent();
  if (content === null) {
    return null;
  }

  return (
    <>
      <div
        className={cn(
          'flex max-h-40 min-h-9 items-start justify-between overflow-y-auto rounded-lg border p-2 px-3 transition-colors duration-500',
          colors.bg,
          colors.border,
        )}
      >
        <div
          className={cn(
            'flex w-full items-center justify-between transition-all duration-150',
            isTransitioning ? 'translate-y-[-8px] opacity-0' : 'translate-y-0 opacity-100',
          )}
        >
          {content}
        </div>
        {canDismiss && (
          <button
            onClick={() => onDismiss(uploadId)}
            className={cn('shrink-0 rounded p-0.5 transition-colors', colors.text, colors.textHover)}
            aria-label="Dismiss"
          >
            <Icon className="lucide--x h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {detailModalOpen && (
        <UploadDetailModal
          open={detailModalOpen}
          onOpenChange={setDetailModalOpen}
          matterId={matterId}
          uploadId={uploadId}
          unpackingSkippedCount={sseProgress.unpackingSkippedCount}
          duplicateIncludedCount={duplicateIncludedCount}
        />
      )}
    </>
  );
}
